/* eslint-disable no-nested-ternary */
import React, { useState, useMemo, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useForm, FormProvider } from 'react-hook-form';
import {
  FunnelSimple,
  Users,
  X,
  Buildings,
  IdentificationCard,
  ArrowUp,
  ArrowDown,
} from 'phosphor-react';
import { FilterDrawer } from '../../components/FilterDrawer';
import { InputForm } from '../../components/inputs/forms/InputForm';
import SelectInputForm from '../../components/inputs/forms/SelectInputForm';
import { SearchModeBar, SearchModeOption } from '../../components/SearchModeBar';
import { useCompanies } from '../../hooks/useCompanies';
import {
  useAnaliseSocios,
  IEmpresaAnaliseSocios,
  ITotalizadoresAnaliseSocios,
  StatusFilterAnaliseSocios,
  ISocioDominio,
  ISocioGov,
} from '../../hooks/useAnaliseSocios';
import { IData, ISelect } from '../../types/shared';
import { formatCNPJ, getFormattedLabelString } from '../../utils/FormatFunctions';
import { cleanFilters } from '../../utils/HandleFunctions';

type SearchMode = 'id' | 'nome' | 'cnpj';

const SEARCH_MODES: SearchModeOption[] = [
  { key: 'id', buttonLabel: 'ID', menuLabel: 'ID (external_id)', placeholder: 'Digite o ID…' },
  {
    key: 'nome',
    buttonLabel: 'Nome',
    menuLabel: 'Nome / Razão social',
    placeholder: 'Buscar por nome ou razão social…',
  },
  { key: 'cnpj', buttonLabel: 'CNPJ', menuLabel: 'CNPJ', placeholder: 'Digite o CNPJ…' },
];

type ViewMode = 'grid' | 'table';
type SortField = 'empresa_id' | 'diferenca' | 'qtd_socios_dominio' | 'qtd_socios_gov';
type SortDirection = 'asc' | 'desc' | null;

interface FilterFormValues {
  cidade: ISelect<string> | null;
  uf: ISelect<string> | null;
  grupo_de_empresas: ISelect<string> | null;
  tipo_regime: ISelect<string> | null;
  dt_cli_desde: string | null;
  dt_cli_ate: string | null;
}

interface AppliedFilters {
  cidade: string | null;
  uf: string | null;
  grupo_de_empresas: string | null;
  tipo_regime: string | null;
  dt_cli_desde: string | null;
  dt_cli_ate: string | null;
}

const emptyFilters: AppliedFilters = {
  cidade: null,
  uf: null,
  grupo_de_empresas: null,
  tipo_regime: null,
  dt_cli_desde: null,
  dt_cli_ate: null,
};

const tipoRegimeOptions: ISelect<string>[] = [
  { value: 'simples_nacional', label: 'Simples Nacional' },
  { value: 'lucro_presumido', label: 'Lucro Presumido' },
  { value: 'lucro_real', label: 'Lucro Real' },
  { value: 'domesticas', label: 'Domésticas' },
  { value: 'mei', label: 'MEI' },
  { value: 'imune_isenta', label: 'Imune/Isenta' },
  { value: 'cnpj_suspenso', label: 'CNPJ Suspenso' },
  { value: 'sem_movimento', label: 'Sem Movimento' },
];

export function AnaliseSociosPage() {
  const { getEmpresasAnaliseSocios, getTotalizadoresAnaliseSocios } = useAnaliseSocios();
  const { getCompaniesFilters } = useCompanies();
  const [searchParams] = useSearchParams();

  // Lê parâmetros da URL para pré-filtrar
  const externalIdFromUrl = searchParams.get('external_id');
  const statusFilterFromUrl = searchParams.get('status_filter') as StatusFilterAnaliseSocios | null;

  const [searchTerm, setSearchTerm] = useState(externalIdFromUrl || '');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(externalIdFromUrl || '');
  const [searchMode, setSearchMode] = useState<SearchMode>(externalIdFromUrl ? 'id' : 'id');
  const [statusFilter, setStatusFilter] = useState<StatusFilterAnaliseSocios>(
    statusFilterFromUrl || 'todas',
  );
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState<AppliedFilters>(emptyFilters);
  const [page, setPage] = useState(1);
  const [selectedEmpresa, setSelectedEmpresa] = useState<IEmpresaAnaliseSocios | null>(null);
  const [sortField, setSortField] = useState<SortField | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>(null);

  const handleSort = (field: SortField, direction: 'asc' | 'desc') => {
    if (sortField === field && sortDirection === direction) {
      // Clicou na mesma seta ativa -> remove ordenação
      setSortField(null);
      setSortDirection(null);
    } else {
      // Ativa ordenação no campo/direção clicado
      setSortField(field);
      setSortDirection(direction);
    }
    setPage(1);
  };

  const renderSortIcon = (field: SortField) => {
    const isActive = sortField === field;
    return (
      <span className="ml-2 inline-flex items-center gap-0.5">
        <button
          type="button"
          onClick={e => {
            e.preventDefault();
            e.stopPropagation();
            handleSort(field, 'asc');
          }}
          className="focus:outline-none"
        >
          <ArrowUp
            size={14}
            weight={isActive && sortDirection === 'asc' ? 'bold' : 'regular'}
            className={`cursor-pointer ${isActive && sortDirection === 'asc' ? 'text-blue-500' : 'text-gray-400 hover:text-gray-600'}`}
          />
        </button>
        <button
          type="button"
          onClick={e => {
            e.preventDefault();
            e.stopPropagation();
            handleSort(field, 'desc');
          }}
          className="focus:outline-none"
        >
          <ArrowDown
            size={14}
            weight={isActive && sortDirection === 'desc' ? 'bold' : 'regular'}
            className={`cursor-pointer ${isActive && sortDirection === 'desc' ? 'text-blue-500' : 'text-gray-400 hover:text-gray-600'}`}
          />
        </button>
      </span>
    );
  };

  // Debounce search term - only update after 1 second of no typing
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      if (searchTerm !== debouncedSearchTerm) {
        setPage(1);
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const filtersFormMethods = useForm<FilterFormValues>({
    defaultValues: {
      cidade: null,
      uf: null,
      grupo_de_empresas: null,
      tipo_regime: null,
      dt_cli_desde: null,
      dt_cli_ate: null,
    },
  });

  // Query para totalizadores
  const { data: totalizadores, isLoading: isLoadingTotais } = useQuery<ITotalizadoresAnaliseSocios>(
    {
      queryKey: ['analiseSociosTotalizadores'],
      queryFn: getTotalizadoresAnaliseSocios,
    },
  );

  // Query para lista de empresas
  const queryFilters = useMemo(() => {
    const filters: Record<string, string> = {
      page: String(page),
      size: '50',
      status_filter: statusFilter,
    };

    if (debouncedSearchTerm) {
      if (searchMode === 'id') filters.external_id = debouncedSearchTerm;
      else if (searchMode === 'cnpj')
        filters.cnpj = debouncedSearchTerm.replace(/[^0-9A-Za-z]/g, '').toUpperCase();
      else filters.razao_social = debouncedSearchTerm;
    }

    // Adiciona filtros aplicados
    Object.entries(appliedFilters).forEach(([key, value]) => {
      if (value) filters[key] = value;
    });

    // Adiciona parâmetros de ordenação
    if (sortField && sortDirection) {
      filters.order_by = sortField;
      filters.order_direction = sortDirection;
    }

    return cleanFilters(filters);
  }, [
    page,
    statusFilter,
    debouncedSearchTerm,
    searchMode,
    appliedFilters,
    sortField,
    sortDirection,
  ]);

  const { data: empresasData, isLoading: isLoadingEmpresas } = useQuery<
    IData<IEmpresaAnaliseSocios[]>
  >({
    queryKey: ['analiseSociosEmpresas', queryFilters],
    queryFn: getEmpresasAnaliseSocios,
  });

  // Buscar opções de filtros
  const { data: companiesFiltersData } = useQuery({
    queryKey: ['CompaniesFilters'],
    queryFn: getCompaniesFilters,
  });

  const empresas = empresasData?.data?.items || [];
  const pagination = empresasData?.data || { page: 1, pages: 1, total: 0 };

  // Contar filtros ativos
  const activeFiltersCount = useMemo(() => {
    return Object.values(appliedFilters).filter(v => v !== null).length;
  }, [appliedFilters]);

  const hasActiveFilters = activeFiltersCount > 0;

  const handleApplyFilters = () => {
    const values = filtersFormMethods.getValues();
    setAppliedFilters({
      cidade: values.cidade?.value || null,
      uf: values.uf?.value || null,
      grupo_de_empresas: values.grupo_de_empresas?.value || null,
      tipo_regime: values.tipo_regime?.value || null,
      dt_cli_desde: values.dt_cli_desde || null,
      dt_cli_ate: values.dt_cli_ate || null,
    });
    setPage(1);
  };

  const handleClearFilters = () => {
    const fields = Object.keys(emptyFilters) as (keyof FilterFormValues)[];
    fields.forEach(field => {
      filtersFormMethods.setValue(field, null);
    });
    setAppliedFilters(emptyFilters);
    setPage(1);
  };

  const handleStatusClick = (status: StatusFilterAnaliseSocios) => {
    setStatusFilter(status);
    setPage(1);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'divergente':
        return { border: 'border-red-200', bg: 'bg-red-50', text: 'text-red-600' };
      case 'convergente':
        return { border: 'border-green-200', bg: 'bg-green-50', text: 'text-green-600' };
      case 'sem_dados_gov':
        return { border: 'border-yellow-200', bg: 'bg-yellow-50', text: 'text-yellow-600' };
      case 'sem_dados_dominio':
        return { border: 'border-yellow-200', bg: 'bg-yellow-50', text: 'text-yellow-600' };
      default:
        return { border: 'border-gray-200', bg: 'bg-gray-50', text: 'text-gray-600' };
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'divergente':
        return 'Divergente';
      case 'convergente':
        return 'Convergente';
      case 'sem_dados_gov':
        return 'Sem dados GOV';
      case 'sem_dados_dominio':
        return 'Sem dados Domínio';
      default:
        return status;
    }
  };

  if (isLoadingTotais || isLoadingEmpresas) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <div className="h-12 w-12 animate-spin rounded-full border-4 border-blue-200 border-t-blue-500" />
        <p className="mt-4 text-gray-500">Carregando dados...</p>
      </div>
    );
  }

  return (
    <div className="flex w-full flex-col gap-2">
      <div className="flex flex-col gap-3 py-5">
        {/* Header */}
        <div className="flex items-center gap-3">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-blue-100">
            <Users className="h-6 w-6 text-blue-600" weight="duotone" />
          </div>
          <div className="flex flex-col justify-center">
            <span className="text-base font-bold">Análise Comparativa de Sócios</span>
            <span className="text-xs text-gray-500">
              Compare sócios cadastrados no Domínio com dados do Governo
            </span>
          </div>
        </div>

        {/* Cards de Estatísticas */}
        <div className="mb-6 flex flex-wrap gap-2 pb-2">
          {/* Total Empresas */}
          <button
            type="button"
            onClick={() => handleStatusClick('todas')}
            className={`stat-card flex cursor-pointer items-center gap-2 rounded-xl p-3 transition-all hover:shadow-md ${
              statusFilter === 'todas' ? 'bg-blue-100 ring-2 ring-blue-500' : 'bg-white'
            }`}
          >
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-blue-100 p-2">
              <svg
                className="h-5 w-5 text-blue-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                />
              </svg>
            </div>
            <div className="flex flex-col">
              <p className="text-[11px] text-[#718EBF]">Total Empresas</p>
              <p className="text-base font-bold text-gray-800">
                {totalizadores?.total_empresas_analisadas || 0}
              </p>
            </div>
          </button>

          {/* Divergentes */}
          <button
            type="button"
            onClick={() => handleStatusClick('divergentes')}
            className={`stat-card flex cursor-pointer items-center gap-2 rounded-xl p-3 transition-all hover:shadow-md ${
              statusFilter === 'divergentes' ? 'bg-red-100 ring-2 ring-red-500' : 'bg-white'
            }`}
          >
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-red-100 p-2">
              <svg
                className="h-5 w-5 text-red-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
            <div className="flex flex-col">
              <p className="text-[11px] text-[#718EBF]">Divergentes</p>
              <p className="text-base font-bold text-red-600">
                {totalizadores?.total_divergentes || 0}
              </p>
              <p className="text-[10px] text-gray-500">
                {totalizadores?.percentual_divergencia?.toFixed(0) || 0}% do total
              </p>
            </div>
          </button>

          {/* Convergentes */}
          <button
            type="button"
            onClick={() => handleStatusClick('convergentes')}
            className={`stat-card flex cursor-pointer items-center gap-2 rounded-xl p-3 transition-all hover:shadow-md ${
              statusFilter === 'convergentes' ? 'bg-green-100 ring-2 ring-green-500' : 'bg-white'
            }`}
          >
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-green-100 p-2">
              <svg
                className="h-5 w-5 text-green-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <div className="flex flex-col">
              <p className="text-[11px] text-[#718EBF]">Convergentes</p>
              <p className="text-base font-bold text-green-600">
                {totalizadores?.total_convergentes || 0}
              </p>
              <p className="text-[10px] text-gray-500">
                {totalizadores?.percentual_convergencia?.toFixed(0) || 0}% do total
              </p>
            </div>
          </button>

          {/* Sem Dados GOV */}
          <button
            type="button"
            onClick={() => handleStatusClick('sem_dados_gov')}
            className={`stat-card flex cursor-pointer items-center gap-2 rounded-xl p-3 transition-all hover:shadow-md ${
              statusFilter === 'sem_dados_gov' ? 'bg-yellow-100 ring-2 ring-yellow-500' : 'bg-white'
            }`}
          >
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-yellow-100 p-2">
              <svg
                className="h-5 w-5 text-yellow-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <div className="flex flex-col">
              <p className="text-[11px] text-[#718EBF]">Sem Dados GOV</p>
              <p className="text-base font-bold text-yellow-600">
                {totalizadores?.total_sem_dados_gov || 0}
              </p>
              <p className="text-[10px] text-gray-500">
                {totalizadores?.percentual_sem_dados_gov?.toFixed(0) || 0}% do total
              </p>
            </div>
          </button>

          {/* Sem Dados Domínio */}
          <button
            type="button"
            onClick={() => handleStatusClick('sem_dados_dominio')}
            className={`stat-card flex cursor-pointer items-center gap-2 rounded-xl p-3 transition-all hover:shadow-md ${
              statusFilter === 'sem_dados_dominio'
                ? 'bg-yellow-100 ring-2 ring-yellow-500'
                : 'bg-white'
            }`}
          >
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-yellow-100 p-2">
              <svg
                className="h-5 w-5 text-yellow-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
                />
              </svg>
            </div>
            <div className="flex flex-col">
              <p className="text-[11px] text-[#718EBF]">Sem Dados Domínio</p>
              <p className="text-base font-bold text-yellow-600">
                {totalizadores?.total_sem_dados_dominio || 0}
              </p>
              <p className="text-[10px] text-gray-500">
                {totalizadores?.percentual_sem_dados_dominio?.toFixed(0) || 0}% do total
              </p>
            </div>
          </button>
        </div>

        {/* Barra de Filtros */}
        <div className="mb-4 rounded-xl bg-white p-3 shadow-sm sm:mb-6 sm:rounded-2xl sm:p-4">
          <div className="flex flex-col gap-3">
            <div className="flex flex-col items-stretch justify-between gap-3 sm:flex-row sm:items-center">
              {/* Busca com Modo */}
              <div className="flex w-full gap-2 sm:w-auto">
                <SearchModeBar
                  modes={SEARCH_MODES}
                  mode={searchMode}
                  onModeChange={m => setSearchMode(m as SearchMode)}
                  value={searchTerm}
                  onValueChange={setSearchTerm}
                />
              </div>

              <div className="flex items-center justify-between gap-2 sm:justify-end">
                <div className="flex gap-2">
                  {/* Toggle View */}
                  <div className="flex overflow-hidden rounded-lg border border-gray-200">
                    <button
                      type="button"
                      onClick={() => setViewMode('grid')}
                      className={`p-1.5 sm:p-2 ${viewMode === 'grid' ? 'bg-blue-500 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                      aria-label="Visualização em grade"
                    >
                      <svg
                        className="h-4 w-4 sm:h-5 sm:w-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"
                        />
                      </svg>
                    </button>
                    <button
                      type="button"
                      onClick={() => setViewMode('table')}
                      className={`p-1.5 sm:p-2 ${viewMode === 'table' ? 'bg-blue-500 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                      aria-label="Visualização em tabela"
                    >
                      <svg
                        className="h-4 w-4 sm:h-5 sm:w-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M4 6h16M4 10h16M4 14h16M4 18h16"
                        />
                      </svg>
                    </button>
                  </div>

                  {/* Filtros Avançados */}
                  <button
                    type="button"
                    onClick={() => setIsFilterDrawerOpen(true)}
                    className={`flex items-center gap-1 rounded-lg border p-1.5 sm:gap-2 sm:p-2 ${
                      hasActiveFilters
                        ? 'border-blue-500 bg-blue-50 text-blue-600'
                        : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    <FunnelSimple className="h-4 w-4 sm:h-5 sm:w-5" />
                    <span className="hidden text-sm sm:inline">Filtros</span>
                    {hasActiveFilters && (
                      <span className="flex h-4 w-4 items-center justify-center rounded-full bg-blue-500 text-[10px] text-white sm:h-5 sm:w-5 sm:text-xs">
                        {activeFiltersCount}
                      </span>
                    )}
                  </button>
                </div>
              </div>
            </div>

            {/* Contador */}
            <div className="text-xs text-gray-500 sm:text-sm">
              Mostrando <span className="font-semibold text-gray-700">{empresas.length}</span> de{' '}
              <span className="font-semibold text-gray-700">{pagination.total || 0}</span> empresas
            </div>
          </div>
        </div>

        {/* Empty State */}
        {empresas.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20">
            <svg
              className="h-16 w-16 text-gray-300"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
              />
            </svg>
            <p className="mt-4 text-lg text-gray-500">Nenhuma empresa encontrada</p>
            <p className="text-sm text-gray-400">Tente ajustar os filtros ou a busca</p>
          </div>
        ) : viewMode === 'grid' ? (
          /* Grid View */
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4 lg:grid-cols-3 xl:grid-cols-4">
            {empresas.map(empresa => {
              const statusColors = getStatusColor(empresa.status);

              return (
                <button
                  type="button"
                  key={empresa.empresa_id}
                  onClick={() => setSelectedEmpresa(empresa)}
                  className={`cursor-pointer rounded-xl border bg-white p-4 text-left transition-all hover:-translate-y-0.5 hover:shadow-lg ${statusColors.border}`}
                >
                  <div className="w-full">
                    <div className="mb-2 flex items-start justify-between gap-2">
                      <span className="shrink-0 rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-600">
                        ID {empresa.empresa_id}
                      </span>
                      <span
                        className={`rounded px-2 py-0.5 text-[10px] font-semibold ${statusColors.bg} ${statusColors.text}`}
                      >
                        {getStatusLabel(empresa.status)}
                      </span>
                    </div>

                    <h3
                      className="mb-1 line-clamp-2 text-sm font-semibold text-gray-800"
                      title={empresa.razao_social}
                    >
                      {empresa.razao_social}
                    </h3>

                    <div className="mb-3 text-[10px] text-gray-500">{formatCNPJ(empresa.cnpj)}</div>

                    <div className="flex items-center justify-between">
                      <div className="flex gap-4">
                        <div className="text-center">
                          <div className="text-lg font-bold text-blue-600">
                            {empresa.qtd_socios_dominio}
                          </div>
                          <div className="text-[10px] text-gray-500">Domínio</div>
                        </div>
                        <div className="text-center">
                          <div className="text-lg font-bold text-purple-600">
                            {empresa.qtd_socios_gov}
                          </div>
                          <div className="text-[10px] text-gray-500">GOV</div>
                        </div>
                        <div className="text-center">
                          <div
                            className={`text-lg font-bold ${empresa.diferenca !== 0 ? 'text-red-600' : 'text-green-600'}`}
                          >
                            {empresa.diferenca > 0 ? `+${empresa.diferenca}` : empresa.diferenca}
                          </div>
                          <div className="text-[10px] text-gray-500">Dif.</div>
                        </div>
                      </div>

                      <svg
                        className="h-5 w-5 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                        />
                      </svg>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        ) : (
          /* Table View */
          <div className="overflow-hidden rounded-xl bg-white shadow-sm sm:rounded-2xl">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[700px]">
                <thead className="border-b border-gray-200 bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-600">
                      <span className="inline-flex items-center">
                        ID
                        {renderSortIcon('empresa_id')}
                      </span>
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-600">
                      Empresa
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-600">
                      CNPJ
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-semibold uppercase text-gray-600">
                      <span className="inline-flex items-center justify-center">
                        Domínio
                        {renderSortIcon('qtd_socios_dominio')}
                      </span>
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-semibold uppercase text-gray-600">
                      <span className="inline-flex items-center justify-center">
                        GOV
                        {renderSortIcon('qtd_socios_gov')}
                      </span>
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-semibold uppercase text-gray-600">
                      <span className="inline-flex items-center justify-center">
                        Diferença
                        {renderSortIcon('diferenca')}
                      </span>
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-semibold uppercase text-gray-600">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {empresas.map(empresa => {
                    const statusColors = getStatusColor(empresa.status);

                    return (
                      <tr
                        key={empresa.empresa_id}
                        onClick={() => setSelectedEmpresa(empresa)}
                        className="cursor-pointer hover:bg-gray-50"
                      >
                        <td className="px-4 py-3 text-sm font-medium text-gray-600">
                          {empresa.empresa_id}
                        </td>
                        <td className="px-4 py-3">
                          <div
                            className="max-w-[200px] truncate text-sm font-medium text-gray-800"
                            title={empresa.razao_social}
                          >
                            {empresa.razao_social}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-xs text-gray-500">{formatCNPJ(empresa.cnpj)}</div>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className="font-semibold text-blue-600">
                            {empresa.qtd_socios_dominio}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className="font-semibold text-purple-600">
                            {empresa.qtd_socios_gov}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span
                            className={`font-bold ${empresa.diferenca !== 0 ? 'text-red-600' : 'text-green-600'}`}
                          >
                            {empresa.diferenca > 0 ? `+${empresa.diferenca}` : empresa.diferenca}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span
                            className={`inline-block rounded-full px-2 py-1 text-[10px] font-semibold ${statusColors.bg} ${statusColors.text}`}
                          >
                            {getStatusLabel(empresa.status)}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Paginação */}
        {pagination.pages > 1 && (
          <div className="mt-4 flex items-center justify-center gap-2">
            <button
              type="button"
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page <= 1}
              aria-label="Página anterior"
              className="rounded-lg border border-gray-200 p-2 text-gray-600 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>
            <span className="text-sm text-gray-600">
              Página {page} de {pagination.pages}
            </span>
            <button
              type="button"
              onClick={() => setPage(p => Math.min(pagination.pages, p + 1))}
              disabled={page >= pagination.pages}
              aria-label="Próxima página"
              className="rounded-lg border border-gray-200 p-2 text-gray-600 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>
          </div>
        )}
      </div>

      {/* FilterDrawer */}
      <FormProvider {...filtersFormMethods}>
        <FilterDrawer
          isOpen={isFilterDrawerOpen}
          onClose={() => setIsFilterDrawerOpen(false)}
          title="Filtros Avançados"
          hasActiveFilters={hasActiveFilters}
          onClearFilters={handleClearFilters}
          onApplyFilters={handleApplyFilters}
          formMethods={filtersFormMethods}
          size="lg"
        >
          <div className="grid grid-cols-2 gap-4">
            <SelectInputForm<ISelect<string>>
              name="cidade"
              placeholder="Filtrar por cidade"
              options={getFormattedLabelString(companiesFiltersData?.cidade)}
              controlMb={0}
              isClearable
            />
            <SelectInputForm<ISelect<string>>
              name="uf"
              placeholder="Filtrar por Estado"
              options={getFormattedLabelString(companiesFiltersData?.uf)}
              controlMb={0}
              isClearable
            />
            <SelectInputForm<ISelect<string>>
              name="grupo_de_empresas"
              placeholder="Filtrar por grupo de empresas"
              options={getFormattedLabelString(companiesFiltersData?.grupo_de_empresas)}
              controlMb={0}
              isClearable
            />
            <SelectInputForm<ISelect<string>>
              name="tipo_regime"
              placeholder="Filtrar por regime tributário"
              options={tipoRegimeOptions}
              controlMb={0}
              isClearable
            />
            <InputForm name="dt_cli_desde" label="Cliente desde" type="date" controlMb={0} />
            <InputForm name="dt_cli_ate" label="Cliente até" type="date" controlMb={0} />
          </div>
        </FilterDrawer>
      </FormProvider>

      {/* Modal de Detalhes dos Sócios */}
      {selectedEmpresa && (
        // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4"
          onClick={() => setSelectedEmpresa(null)}
          onKeyDown={e => e.key === 'Escape' && setSelectedEmpresa(null)}
          role="dialog"
          aria-modal="true"
          tabIndex={-1}
        >
          {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
          <div
            className="max-h-[90vh] w-full max-w-4xl overflow-hidden rounded-2xl bg-white shadow-2xl"
            onClick={e => e.stopPropagation()}
            onKeyDown={e => e.stopPropagation()}
            role="document"
          >
            {/* Header do Modal */}
            <div className="border-b border-gray-200 bg-gray-50 px-6 py-4">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-4">
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-blue-100">
                    <Buildings className="h-6 w-6 text-blue-600" weight="duotone" />
                  </div>
                  <div>
                    <h2 className="text-lg font-bold text-gray-800">
                      {selectedEmpresa.razao_social}
                    </h2>
                    <div className="flex items-center gap-3 text-sm text-gray-500">
                      <span>ID: {selectedEmpresa.empresa_id}</span>
                      <span>•</span>
                      <span>{formatCNPJ(selectedEmpresa.cnpj)}</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span
                    className={`rounded-full px-3 py-1 text-xs font-semibold ${
                      selectedEmpresa.status === 'divergente'
                        ? 'bg-red-100 text-red-600'
                        : selectedEmpresa.status === 'convergente'
                          ? 'bg-green-100 text-green-600'
                          : 'bg-gray-100 text-gray-600'
                    }`}
                  >
                    {getStatusLabel(selectedEmpresa.status)}
                  </span>
                  <button
                    type="button"
                    onClick={() => setSelectedEmpresa(null)}
                    className="rounded-full p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                    aria-label="Fechar modal"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
              </div>
            </div>

            {/* Resumo */}
            <div className="border-b border-gray-200 bg-white px-6 py-4">
              <div className="flex items-center justify-center gap-8">
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-600">
                    {selectedEmpresa.qtd_socios_dominio}
                  </div>
                  <div className="text-xs text-gray-500">Sócios no Domínio</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-purple-600">
                    {selectedEmpresa.qtd_socios_gov}
                  </div>
                  <div className="text-xs text-gray-500">Sócios no GOV</div>
                </div>
                <div className="text-center">
                  <div
                    className={`text-2xl font-bold ${selectedEmpresa.diferenca !== 0 ? 'text-red-600' : 'text-green-600'}`}
                  >
                    {selectedEmpresa.diferenca > 0
                      ? `+${selectedEmpresa.diferenca}`
                      : selectedEmpresa.diferenca}
                  </div>
                  <div className="text-xs text-gray-500">Diferença</div>
                </div>
              </div>
            </div>

            {/* Conteúdo - Comparativo lado a lado */}
            <div className="max-h-[50vh] overflow-y-auto px-6 py-4">
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                {/* Coluna Domínio */}
                <div>
                  <div className="mb-3 flex items-center gap-2">
                    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-100">
                      <IdentificationCard className="h-4 w-4 text-blue-600" />
                    </div>
                    <h3 className="text-sm font-semibold text-blue-600">
                      Sócios no Domínio ({selectedEmpresa.socios_dominio?.length || 0})
                    </h3>
                  </div>
                  <div className="space-y-3">
                    {selectedEmpresa.socios_dominio && selectedEmpresa.socios_dominio.length > 0 ? (
                      selectedEmpresa.socios_dominio.map((socio: ISocioDominio, idx: number) => (
                        <div
                          key={socio.id || idx}
                          className="rounded-lg border border-blue-100 bg-blue-50 p-4"
                        >
                          <p className="mb-2 text-sm font-semibold text-gray-800">
                            {socio.nome_socio}
                          </p>
                          <div className="grid grid-cols-2 gap-2 text-xs">
                            <div className="flex flex-col">
                              <span className="text-gray-400">CPF</span>
                              <span className="font-medium text-gray-700">
                                {socio.cpf?.replace(
                                  /(\d{3})(\d{3})(\d{3})(\d{2})/,
                                  '$1.$2.$3-$4',
                                ) || '-'}
                              </span>
                            </div>
                            <div className="flex flex-col">
                              <span className="text-gray-400">Data Ingresso</span>
                              <span className="font-medium text-gray-700">
                                {socio.data_ingresso
                                  ? new Date(socio.data_ingresso).toLocaleDateString('pt-BR')
                                  : '-'}
                              </span>
                            </div>
                            <div className="flex flex-col">
                              <span className="text-gray-400">Participação</span>
                              <span className="font-medium text-gray-700">
                                {socio.participacao !== undefined ? `${socio.participacao}%` : '-'}
                              </span>
                            </div>
                            <div className="flex flex-col">
                              <span className="text-gray-400">Cotas</span>
                              <span className="font-medium text-gray-700">
                                {socio.qt_cotas?.toLocaleString('pt-BR') || '-'}
                              </span>
                            </div>
                          </div>
                          {socio.data_saida && (
                            <div className="mt-2 rounded bg-red-100 px-2 py-1 text-xs text-red-600">
                              Saída: {new Date(socio.data_saida).toLocaleDateString('pt-BR')}
                            </div>
                          )}
                        </div>
                      ))
                    ) : (
                      <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-center text-sm text-gray-400">
                        Nenhum sócio cadastrado no Domínio
                      </div>
                    )}
                  </div>
                </div>

                {/* Coluna GOV */}
                <div>
                  <div className="mb-3 flex items-center gap-2">
                    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-purple-100">
                      <Users className="h-4 w-4 text-purple-600" />
                    </div>
                    <h3 className="text-sm font-semibold text-purple-600">
                      Sócios no GOV ({selectedEmpresa.socios_gov?.length || 0})
                    </h3>
                  </div>
                  <div className="space-y-3">
                    {selectedEmpresa.socios_gov && selectedEmpresa.socios_gov.length > 0 ? (
                      selectedEmpresa.socios_gov.map((socio: ISocioGov, idx: number) => {
                        // Verifica se está faltando no Domínio
                        const isMissing = selectedEmpresa.socios_faltantes_dominio?.some(
                          s => s.cpf === socio.cpf,
                        );
                        return (
                          <div
                            key={socio.id || idx}
                            className={`rounded-lg border p-4 ${
                              isMissing
                                ? 'border-orange-200 bg-orange-50'
                                : 'border-purple-100 bg-purple-50'
                            }`}
                          >
                            <div className="mb-2 flex items-start justify-between">
                              <p className="text-sm font-semibold text-gray-800">{socio.nome}</p>
                              {isMissing && (
                                <span className="shrink-0 rounded-full bg-orange-100 px-2 py-0.5 text-[10px] font-semibold text-orange-600">
                                  Faltando no Domínio
                                </span>
                              )}
                            </div>
                            <div className="grid grid-cols-2 gap-2 text-xs">
                              <div className="flex flex-col">
                                <span className="text-gray-400">CPF</span>
                                <span className="font-medium text-gray-700">
                                  {socio.cpf || '-'}
                                </span>
                              </div>
                              <div className="flex flex-col">
                                <span className="text-gray-400">Sócio Desde</span>
                                <span className="font-medium text-gray-700">
                                  {socio.socio_desde || '-'}
                                </span>
                              </div>
                              {socio.cargo && (
                                <div className="col-span-2 flex flex-col">
                                  <span className="text-gray-400">Cargo</span>
                                  <span className="font-medium text-gray-700">{socio.cargo}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-center text-sm text-gray-400">
                        Nenhum sócio encontrado nos dados do Governo
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Sócios faltantes destacados */}
              {selectedEmpresa.socios_faltantes_dominio &&
                selectedEmpresa.socios_faltantes_dominio.length > 0 && (
                  <div className="mt-6">
                    <div className="mb-3 flex items-center gap-2">
                      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-orange-100">
                        <svg
                          className="h-4 w-4 text-orange-600"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                          />
                        </svg>
                      </div>
                      <h3 className="text-sm font-semibold text-orange-600">
                        Ação Necessária: {selectedEmpresa.socios_faltantes_dominio.length} sócio(s)
                        do GOV não cadastrado(s) no Domínio
                      </h3>
                    </div>
                    <div className="rounded-lg border border-orange-200 bg-orange-50 p-4">
                      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                        {selectedEmpresa.socios_faltantes_dominio.map(
                          (socio: ISocioGov, idx: number) => (
                            <div
                              key={socio.id || idx}
                              className="flex items-center gap-2 rounded bg-white p-2 text-sm"
                            >
                              <span className="font-medium text-gray-800">{socio.nome}</span>
                              <span className="text-gray-400">•</span>
                              <span className="text-gray-500">{socio.cpf}</span>
                            </div>
                          ),
                        )}
                      </div>
                    </div>
                  </div>
                )}
            </div>

            {/* Footer do Modal */}
            <div className="border-t border-gray-200 bg-gray-50 px-6 py-4">
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={() => setSelectedEmpresa(null)}
                  className="rounded-lg bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200"
                >
                  Fechar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
